class FeaturedVideo extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.mediaItems = [];
    this.progressIntervals = new Map();
    this.isDesktop = window.innerWidth >= 990;
    this.imageDisplayDuration = 5000;
    this.completedItems = new Set();

    this.handleResize = this.handleResize.bind(this);
    this.handleThumbnailClick = this.handleThumbnailClick.bind(this);
    this.playNext = this.playNext.bind(this);
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  init() {
    this.setupMediaItems();
    this.setupEventListeners();
    this.hideAllMedia();
    this.resetAllProgress();
    this.activateMedia(0);
  }

  setupMediaItems() {
    const contentItems = this.querySelectorAll('.featured-video__content-item');

    contentItems.forEach((content, index) => {
      const thumbnailCard = this.querySelectorAll('.featured-video__thumbnails-card')[index];
      const desktopMedia = this.querySelector(`[data-media-index="${index}-desktop"]`);
      const mobileMedia = this.querySelector(`[data-media-index="${index}-mobile"]`);
      const isVideo = desktopMedia && desktopMedia.tagName === 'VIDEO';

      this.mediaItems.push({
        index,
        isVideo,
        desktopMedia: desktopMedia || this.getImageElement(index, true),
        mobileMedia: mobileMedia || this.getImageElement(index, false),
        content,
        thumbnailCard,
        progressOverlay: thumbnailCard?.querySelector('.featured-video__thumbnail-progress-overlay'),
        duration: isVideo ? null : this.imageDisplayDuration,
      });
    });

    this.mediaItems.forEach((item) => {
      if (item.isVideo) {
        this.setupVideoListeners(item);
      }
    });
  }

  getImageElement(index, isDesktop) {
    const containerClass = isDesktop
      ? '.featured-video__media-container--desktop'
      : '.featured-video__media-container--mobile';

    const containers = this.querySelectorAll(containerClass);
    return containers[index]?.querySelector('img');
  }

  setupVideoListeners(item) {
    [item.desktopMedia, item.mobileMedia].forEach((video) => {
      if (video && video.tagName === 'VIDEO') {
        video.addEventListener('loadedmetadata', () => {
          item.duration = video.duration * 1000;
        });

        video.addEventListener('ended', () => {
          if (item.index === this.currentIndex) {
            this.playNext();
          }
        });

        video.addEventListener('error', () => {
          if (item.index === this.currentIndex) {
            this.playNext();
          }
        });
      }
    });
  }

  setupEventListeners() {
    this.mediaItems.forEach((item) => {
      if (item.thumbnailCard) {
        item.thumbnailCard.addEventListener('click', () => {
          this.handleThumbnailClick(item.index);
        });
      }
    });

    window.addEventListener('resize', this.handleResize);
  }

  handleResize() {
    const wasDesktop = this.isDesktop;
    this.isDesktop = window.innerWidth >= 990;

    if (wasDesktop !== this.isDesktop) {
      this.hideAllMedia();
      this.activateMedia(this.currentIndex);
    }
  }

  handleThumbnailClick(index) {
    this.clearCurrentProgress();
    
    if (index !== this.currentIndex) {
      if (index > this.currentIndex) {
        // Forward navigation: complete all previous videos including current
        this.markAsCompleted(this.currentIndex);
        this.markSequentialCompletion(index);
      } else {
        // Backward navigation: reset all videos after the clicked one
        this.resetForwardProgress(index);
        this.markSequentialCompletion(index);
      }
      this.activateMedia(index);
    } else {
      this.restartCurrentMedia();
    }
  }

  resetForwardProgress(fromIndex) {
    for (let i = fromIndex + 1; i < this.mediaItems.length; i++) {
      this.completedItems.delete(i);
      this.resetProgress(i);
    }
  }

  markSequentialCompletion(targetIndex) {
    for (let i = 0; i < targetIndex; i++) {
      this.completedItems.add(i);
      this.setProgressCompleted(i);
    }
  }

  restartCurrentMedia() {
    const currentItem = this.mediaItems[this.currentIndex];
    if (!currentItem) return;

    this.clearCurrentProgress();

    if (currentItem.progressOverlay) {
      currentItem.progressOverlay.style.transform = 'scaleX(0)';
    }

    this.completedItems.delete(this.currentIndex);

    const currentMedia = this.isDesktop ? currentItem.desktopMedia : currentItem.mobileMedia;
    if (currentMedia && currentMedia.tagName === 'VIDEO') {
      currentMedia.currentTime = 0;
      currentMedia.play().catch(() => {});
    }

    this.startProgress(currentItem);
  }

  activateMedia(index) {
    if (this.currentIndex === this.mediaItems.length - 1 && index === 0) {
      this.startNewCycle();
    }

    this.clearCurrentProgress();
    this.deactivateCurrentMedia();
    this.currentIndex = index;
    const currentItem = this.mediaItems[index];
    if (!currentItem) return;

    this.activateMediaElement(currentItem);
    this.activateContent(currentItem);
    this.scrollThumbnailIntoView(currentItem);

    if (this.completedItems.has(index)) {
      this.resetProgress(index);
    }

    this.startProgress(currentItem);
  }

  hideAllMedia() {
    const allMediaContainers = this.querySelectorAll(
      '.featured-video__media-container--desktop, .featured-video__media-container--mobile'
    );

    allMediaContainers.forEach((container) => {
      const mediaElements = container.querySelectorAll('img, video');
      mediaElements.forEach((media) => {
        media.classList.remove('active');
        if (media.tagName === 'VIDEO') {
          media.pause();
          media.currentTime = 0;
        }
      });
    });
  }

  deactivateCurrentMedia() {
    const allMediaContainers = this.querySelectorAll(
      '.featured-video__media-container--desktop, .featured-video__media-container--mobile'
    );

    allMediaContainers.forEach((container) => {
      const mediaElements = container.querySelectorAll('img, video');
      mediaElements.forEach((media) => {
        media.classList.remove('active');
        if (media.tagName === 'VIDEO') {
          media.pause();
          media.currentTime = 0;
        }
      });
    });

    this.querySelectorAll('.featured-video__content-item').forEach((content) => {
      content.classList.remove('active');
    });
  }

  activateMediaElement(item) {
    const currentMedia = this.isDesktop ? item.desktopMedia : item.mobileMedia;

    if (currentMedia) {
      currentMedia.classList.add('active');

      if (item.isVideo && currentMedia.tagName === 'VIDEO') {
        currentMedia.currentTime = 0;

        if (currentMedia.readyState >= 2) {
          const playPromise = currentMedia.play();
          if (playPromise !== undefined) {
            playPromise.catch(() => {
              this.playNext();
            });
          }
        } else {
          const onCanPlay = () => {
            currentMedia.removeEventListener('canplay', onCanPlay);
            const playPromise = currentMedia.play();
            if (playPromise !== undefined) {
              playPromise.catch(() => {
                this.playNext();
              });
            }
          };
          currentMedia.addEventListener('canplay', onCanPlay);
        }
      }
    }
  }

  activateContent(item) {
    if (item.content) {
      item.content.classList.add('active');
    }
  }

  startProgress(item) {
    if (!item.progressOverlay) return;

    item.progressOverlay.style.transform = 'scaleX(0)';

    let duration = item.duration;

    if (item.isVideo) {
      const currentMedia = this.isDesktop ? item.desktopMedia : item.mobileMedia;
      if (currentMedia && currentMedia.tagName === 'VIDEO') {
        if (currentMedia.duration && !isNaN(currentMedia.duration) && currentMedia.duration > 0) {
          duration = currentMedia.duration * 1000;
          item.duration = duration;
          this.animateProgress(item, duration);
        } else {
          const checkDuration = () => {
            if (currentMedia.duration && !isNaN(currentMedia.duration) && currentMedia.duration > 0) {
              duration = currentMedia.duration * 1000;
              item.duration = duration;
              this.animateProgress(item, duration);
            } else {
              setTimeout(checkDuration, 100);
            }
          };
          checkDuration();
        }
        return;
      }
    }

    if (!item.isVideo) {
      duration = this.imageDisplayDuration;
      item.duration = duration;
    }

    if (duration) {
      this.animateProgress(item, duration);
    }
  }

  animateProgress(item, duration) {
    const progressOverlay = item.progressOverlay;
    if (!progressOverlay || !duration) return;

    const startTime = Date.now();
    const currentMedia = this.isDesktop ? item.desktopMedia : item.mobileMedia;

    const updateProgress = () => {
      if (item.index !== this.currentIndex) {
        return;
      }

      const elapsed = Date.now() - startTime;
      let progress;

      if (item.isVideo && currentMedia && currentMedia.tagName === 'VIDEO') {
        const videoDuration = duration / 1000;
        progress = currentMedia.currentTime / videoDuration;
      } else {
        progress = elapsed / duration;
      }

      progress = Math.min(Math.max(progress, 0), 1);
      progressOverlay.style.transform = `scaleX(${progress})`;

      if (progress < 1 && item.index === this.currentIndex) {
        this.progressIntervals.set(item.index, requestAnimationFrame(updateProgress));
      } else if (progress >= 1 && item.index === this.currentIndex) {
        this.playNext();
      }
    };

    if (this.progressIntervals.has(item.index)) {
      cancelAnimationFrame(this.progressIntervals.get(item.index));
    }

    this.progressIntervals.set(item.index, requestAnimationFrame(updateProgress));
  }

  markAsCompleted(index) {
    this.completedItems.add(index);
    this.setProgressCompleted(index);
  }

  startNewCycle() {
    this.completedItems.clear();
    this.resetAllProgress();
  }

  setProgressCompleted(index) {
    const item = this.mediaItems[index];
    if (item && item.progressOverlay) {
      item.progressOverlay.style.transform = 'scaleX(1)';
    }
  }

  resetProgress(index) {
    const item = this.mediaItems[index];
    if (item && item.progressOverlay) {
      item.progressOverlay.style.transform = 'scaleX(0)';
    }
  }

  resetAllProgress() {
    this.mediaItems.forEach((item) => {
      if (item.progressOverlay) {
        item.progressOverlay.style.transform = 'scaleX(0)';
      }
    });
  }

  clearCurrentProgress() {
    if (this.progressIntervals.has(this.currentIndex)) {
      cancelAnimationFrame(this.progressIntervals.get(this.currentIndex));
      this.progressIntervals.delete(this.currentIndex);
    }
  }

  playNext() {
    this.markAsCompleted(this.currentIndex);
    const nextIndex = (this.currentIndex + 1) % this.mediaItems.length;
    this.activateMedia(nextIndex);
  }

  clearAllProgress() {
    this.progressIntervals.forEach((animationId) => {
      cancelAnimationFrame(animationId);
    });
    this.progressIntervals.clear();
  }

  scrollThumbnailIntoView(item) {
    if (!item.thumbnailCard) return;

    const thumbnailWrapper = this.querySelector('.featured-video__thumbnails-card-wrapper');
    if (!thumbnailWrapper) return;

    const card = item.thumbnailCard;
    const wrapper = thumbnailWrapper;

    const cardRect = card.getBoundingClientRect();
    const wrapperRect = wrapper.getBoundingClientRect();

    const cardLeft = card.offsetLeft;
    const cardRight = cardLeft + card.offsetWidth;
    const wrapperScrollLeft = wrapper.scrollLeft;
    const wrapperWidth = wrapper.clientWidth;
    const wrapperScrollRight = wrapperScrollLeft + wrapperWidth;

    const isFullyVisible = cardLeft >= wrapperScrollLeft && cardRight <= wrapperScrollRight;

    if (!isFullyVisible) {
      let targetScrollLeft;

      if (cardLeft < wrapperScrollLeft) {
        targetScrollLeft = cardLeft - 12;
      } else {
        targetScrollLeft = cardRight - wrapperWidth + 12;
      }

      targetScrollLeft = Math.max(0, Math.min(targetScrollLeft, wrapper.scrollWidth - wrapperWidth));

      wrapper.scrollTo({
        left: targetScrollLeft,
        behavior: 'smooth',
      });
    }
  }

  cleanup() {
    this.clearAllProgress();
    window.removeEventListener('resize', this.handleResize);

    this.mediaItems.forEach((item) => {
      if (item.isVideo) {
        [item.desktopMedia, item.mobileMedia].forEach((video) => {
          if (video && video.tagName === 'VIDEO') {
            video.pause();
            video.classList.remove('active');
          }
        });
      }
    });

    this.hideAllMedia();
  }
}

customElements.define('featured-video', FeaturedVideo);
